import React, { useState, useRef, useEffect } from 'react';
import { LANGUAGES } from '../lib/constants';

export const CheckinTranslationBar = ({
  selectedLang,
  onSelectLang,
  showOriginal,
  onToggleOriginal,
  isTranslating,
  targetLangLabel,
}: {
  selectedLang: string;
  onSelectLang: (langCode: string) => void;
  showOriginal: boolean;
  onToggleOriginal: () => void;
  isTranslating: boolean;
  targetLangLabel: string;
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isNonEnglish = selectedLang && selectedLang !== 'en';
  const filteredLangs = LANGUAGES.filter(l => 
    l.label.toLowerCase().includes(search.toLowerCase()) || 
    l.code.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="relative bg-gradient-to-r from-indigo-50/90 to-purple-50/90 border border-indigo-100 rounded-xl p-2.5 sm:px-3 sm:py-2 flex flex-wrap items-center justify-between gap-2 text-xs shadow-xs">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0">
          <span className="material-icons-round text-sm">translate</span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {isTranslating ? (
            <span className="flex items-center gap-1 text-indigo-700 font-semibold">
              <span className="material-icons-round text-xs animate-spin">refresh</span>
              Translating...
            </span>
          ) : isNonEnglish && !showOriginal ? (
            <span className="text-indigo-900 font-medium">
              Translated to <strong className="text-indigo-700 font-bold">{targetLangLabel}</strong>
            </span>
          ) : isNonEnglish && showOriginal ? (
            <span className="text-gray-600 font-medium">
              Showing <strong className="text-gray-800 font-bold">English (Original)</strong>
            </span>
          ) : (
            <span className="text-gray-600 font-medium">
              Language: <strong className="text-gray-800 font-bold">English</strong>
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 ml-auto">
        {isNonEnglish && (
          <button
            type="button"
            onClick={onToggleOriginal}
            className="px-2.5 py-1 bg-white text-indigo-700 border border-indigo-200 rounded-lg font-bold hover:bg-indigo-50 transition-colors flex items-center gap-1 text-xs shadow-2xs"
            title={showOriginal ? 'Switch back to translated version' : 'View original English text'}
          >
            <span className="material-icons-round text-[13px]">
              {showOriginal ? 'translate' : 'visibility'}
            </span>
            {showOriginal ? 'See Translation' : 'Show Original'}
          </button>
        )}

        {/* Change Language Selector Button */}
        <div className="relative" ref={pickerRef}>
          <button
            type="button"
            onClick={() => setShowPicker(!showPicker)}
            className="px-2.5 py-1 bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg font-bold transition-colors flex items-center gap-1 text-xs shadow-2xs"
            title="Change translation language"
          >
            <span>{targetLangLabel}</span>
            <span className="material-icons-round text-[14px]">arrow_drop_down</span>
          </button>

          {showPicker && (
            <div className="absolute right-0 top-full mt-1.5 w-64 bg-white rounded-xl shadow-xl border border-gray-200 p-2 z-50">
              <div className="mb-2">
                <input
                  type="text"
                  placeholder="Search language..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500"
                  autoFocus
                />
              </div>
              <div className="max-h-48 overflow-y-auto space-y-0.5 divide-y divide-gray-50">
                {filteredLangs.map(lang => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => {
                      onSelectLang(lang.code);
                      setShowPicker(false);
                      setSearch('');
                    }}
                    className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs flex items-center justify-between transition-colors ${
                      lang.code === selectedLang 
                        ? 'bg-indigo-50 text-indigo-700 font-bold' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span>{lang.label}</span>
                    {lang.code === selectedLang && (
                      <span className="material-icons-round text-xs text-indigo-600">check</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
